import clsx from 'clsx'
import type { ComponentProps } from 'react'
import type { CurrencyCode } from '@/services/graphql/generated'
import { useFormatter } from '@/utils/useFormatter'
import { Text } from 'ui/src/components/Text/Text'
import { xStack } from 'ui/src/patterns/stack'

/**
 * Renders styled monthly price with strikethrough support.
 * Visually displays the abbreviated format (e.g. "110 kr/mån") while providing
 * an unabbreviated aria-label for screen readers (e.g. "110 kr per månad").
 */

type Props = ComponentProps<'span'> & {
  currencyCode: CurrencyCode
  amount: number
  reducedAmount?: number
  color?: ComponentProps<typeof Text>['color']
  secondaryColor?: ComponentProps<typeof Text>['color']
  showPlusIcon?: boolean
}

export const Price = ({
  color = 'textPrimary',
  secondaryColor = 'textSecondary',
  reducedAmount,
  amount,
  currencyCode,
  className,
  showPlusIcon = false,
  ...props
}: Props) => {
  const formatter = useFormatter()
  const effectivePrice = { currencyCode, amount: reducedAmount ?? amount }
  const accessibleLabel = formatter.monthlyPriceAccessible(effectivePrice)

  return (
    <span className={clsx(xStack({ gap: 'xs' }), className)} role="text" aria-label={accessibleLabel} {...props}>
      {reducedAmount !== undefined && (
        <Text as="span" size="md" strikethrough={true} color={secondaryColor} aria-hidden={true}>
          {showPlusIcon && '+'}
          {formatter.monthlyPrice({
            amount,
            currencyCode,
          })}
        </Text>
      )}
      <Text as="span" size="md" color={color} aria-hidden={true}>
        {showPlusIcon && '+'}
        {formatter.monthlyPrice(effectivePrice)}
      </Text>
    </span>
  )
}
